import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { formatISO } from 'date-fns/formatISO'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'
import { keyframes } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'

import { H2, Icon, P } from '@mxenabled/mxui'

import StepOne from './components/onboarding/StepOne'
import StepTwo from './components/onboarding/StepTwo'

import {
  Loader,
  useCashflowStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useScreenSize,
  useTransactionStore,
  useUserStore,
} from '../common'
import { type RepeatingTransaction, type Transaction, RepeatingType, Frequency } from '../core'
import SubheaderRow from '../common/components/SubheaderRow'

import { useCashflowUiStore } from './context/hooks'

import { TransactionRow } from '../transactions'
import { incomeFilter, incomeFilterSearch } from '../transactions/utils/Filters'

import { AddRecurringTransactionDetails } from '../recurringtransactions'
import { SelectTransaction } from '../recurringtransactions'
import { getNextPaymentDate } from '../core/utils/RecurringTransactionsUtil'

import { fromUnixToDayOfWeekAndDate } from '../core/utils/DateUtil'
import { CASHFLOW_IMAGES } from './constants/CashflowImages'

interface UpcomingTransactions extends Transaction {
  nextDate: Date | null
  frequency: Frequency
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const CashflowOnboarding: React.FC = () => {
  const { addRepeatingTransaction, repeatingTransactions } = useRecurringTransactionsStore()
  const { cashflow: customCopy, common: commonCopy } = useGlobalCopyStore()
  const { user } = useUserStore()
  const { completeOnboarding } = useCashflowStore()
  const { isDirty } = useCashflowUiStore()
  const { isMobile } = useScreenSize()
  const { isCopyLoaded, setDisplayedDate } = useGlobalUiStore()
  const { filter, setFilter, transactions } = useTransactionStore()
  const theme = useTheme()

  const [addIncome, setAddIncome] = useState(true)
  const [frequency, setFrequency] = React.useState(Frequency.EveryMonth)
  const [firstDay, setFirstDay] = React.useState(1)
  const [secondDay, setSecondDay] = React.useState(15)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // const [nextDate, setNextDate] = React.useState<Date | null>(null)
  const [step, setStep] = useState(1)
  const [selectedTransactions, setSelectedTransactions] = useState<UpcomingTransactions[] | []>([])
  const [transactionGuid, setTransactionGuid] = useState('')

  const transaction = React.useMemo(
    () => transactions.find((t) => t.guid === transactionGuid),
    [transactionGuid, transactions],
  )

  const canSubmit = !!repeatingTransactions.filter(
    (r) => r.repeating_transaction_type === RepeatingType.Income,
  ).length

  const handleGetTransactions = async () => {
    setStep(2)
    // set transaction date range and filter by income
    setDisplayedDate(
      //  Beginning of the month, 90 days before today
      startOfMonth(subDays(startOfToday(), 90)),
      startOfToday(),
    )

    setFilter({ ...filter, custom: incomeFilter })

    setStep(3)
  }

  const handleAddIncome = async () => {
    if (selectedTransactions.length < 1) {
      return
    }
    setIsSubmitting(true)

    // Create Repeating Transactions
    for (let i = 0; i < selectedTransactions.length; i++) {
      const upcomingTransaction = selectedTransactions[i]

      const nextDate = getNextPaymentDate(frequency, fromUnixTime(upcomingTransaction.date))

      const repeatingTransaction = {
        account_guid: upcomingTransaction.account_guid,
        amount: upcomingTransaction.amount,
        created_by: 0, //user
        merchant_guid: upcomingTransaction.merchant_guid,
        description: upcomingTransaction.feed_description || upcomingTransaction.description,
        predicted_occurs_on: formatISO(nextDate, { representation: 'date' }),
        recurrence_day: nextDate.getDay(),
        recurrence_type: frequency,
        repeating_transaction_type: RepeatingType.Income,
        second_recurrence_day: secondDay,
        transaction_type: upcomingTransaction.transaction_type,
        user_guid: user.guid,
      } as RepeatingTransaction

      await addRepeatingTransaction(repeatingTransaction, upcomingTransaction)
    }
    await completeOnboarding()
  }

  const showSelectIncome =
    (!transaction && addIncome) || (!transaction && selectedTransactions.length < 1)

  if (!isCopyLoaded) {
    return <Loader />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: !isMobile ? 'center' : 'flex-start',
        backgroundColor: theme.palette.background.paper,
        backgroundImage: !isMobile ? `url(${CASHFLOW_IMAGES.CASHFLOW_ONBOARDING})` : null,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left 100px',
        backgroundSize: '35%',
        py: 24,
      }}
    >
      {step === 1 && (
        <Box
          sx={{
            ...(isMobile
              ? { display: 'flex', flexDirection: 'column', maxWidth: '100%', padding: '0 16px' }
              : { maxWidth: 590, ml: '40%', mr: 90 }),
          }}
        >
          <StepOne handleNextStep={handleGetTransactions} />
        </Box>
      )}
      {step === 2 && (
        <Box
          sx={{
            ...(isMobile
              ? { display: 'flex', flexDirection: 'column', maxWidth: '100%', padding: '0 16px' }
              : { maxWidth: 590, ml: '40%', mr: 90 }),
          }}
        >
          <StepTwo />
        </Box>
      )}
      {step === 3 && (
        <Box
          sx={{
            ...(isMobile
              ? { display: 'flex', flexDirection: 'column', maxWidth: '100%', padding: '0 16px' }
              : { maxWidth: 590, ml: '40%', mr: 90 }),
          }}
        >
          <H2 pb={12} variant="h1">
            {customCopy.add_income_title}
          </H2>
          <P mb={24}>{customCopy.add_income_description}</P>

          {selectedTransactions.length > 0 && !transaction && (
            <>
              <List>
                {selectedTransactions.map((transaction, index) => {
                  return (
                    <React.Fragment key={transaction.guid}>
                      <SubheaderRow bgcolor="background.paper">
                        {fromUnixToDayOfWeekAndDate(
                          getUnixTime(transaction.nextDate || transaction.date),
                        )}
                      </SubheaderRow>
                      <TransactionRow transaction={transaction} />
                      {index !== selectedTransactions.length - 1 && <Divider />}
                    </React.Fragment>
                  )
                })}
              </List>
              <Stack alignItems="center" direction="row">
                <Divider sx={{ flexGrow: 1 }} />
                <Button
                  onClick={() => setAddIncome(true)}
                  startIcon={<Icon name="add" size={20} />}
                >
                  {customCopy.add_income}
                </Button>
              </Stack>
            </>
          )}

          {showSelectIncome && (
            <SelectTransaction
              bgcolor="background.paper"
              onSearch={(searchValue) =>
                setFilter({
                  ...filter,
                  custom: (transaction: Transaction) =>
                    searchValue
                      ? incomeFilterSearch(transaction, searchValue)
                      : incomeFilter(transaction),
                })
              }
              onSelected={(guid) => {
                setTransactionGuid(guid)
                setAddIncome(false)
              }}
            />
          )}

          {!canSubmit && isDirty && (
            <P color="error" mt={16}>
              {customCopy.confirm_error}
            </P>
          )}

          {transaction && (
            <>
              <AddRecurringTransactionDetails
                bgcolor="background.paper"
                firstDay={firstDay}
                frequency={frequency}
                onFirstDayChange={setFirstDay}
                onFrequencyChange={setFrequency}
                onSecondDayChange={setSecondDay}
                secondDay={secondDay}
                transaction={transaction}
              />
              <Stack direction="row" gap={16} ml={24} mt={32}>
                <Button
                  aria-disabled={!transaction}
                  onClick={() => {
                    const updatedTransactions = [...selectedTransactions]
                    updatedTransactions.push({
                      ...transaction,
                      nextDate: getNextPaymentDate(frequency, fromUnixTime(transaction.date)),
                      frequency,
                    })

                    setSelectedTransactions(updatedTransactions)
                    setTransactionGuid('')
                  }}
                  variant="contained"
                >
                  {commonCopy.confirm_button}
                </Button>
                <Button
                  onClick={() => {
                    setTransactionGuid('')
                    setAddIncome(true)
                    handleGetTransactions().finally()
                  }}
                  variant="outlined"
                >
                  {commonCopy.cancel_button}
                </Button>
              </Stack>
            </>
          )}

          {selectedTransactions.length > 0 && !transaction && (
            <Stack direction="row" gap={16} ml={24} mt={32}>
              <Button
                aria-disabled={selectedTransactions.length < 1 || isSubmitting}
                onClick={handleAddIncome}
                startIcon={
                  isSubmitting ? (
                    <Icon
                      name="progress_activity"
                      sx={{
                        animation: `${spin} 2s linear infinite`,
                      }}
                    />
                  ) : null
                }
                variant="contained"
              >
                {customCopy.confirm_income_button}
              </Button>
              <Button onClick={completeOnboarding} variant="outlined">
                {customCopy.skip_button}
              </Button>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  )
}

export default observer(CashflowOnboarding)
